import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getCheckoutStatus } from '@/lib/api';
import { CheckCircle2, XCircle, Loader2, BookOpen, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState(null);
  const [polling, setPolling] = useState(true);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId || !polling) return;
    const maxAttempts = 8;
    const poll = async () => {
      try {
        const res = await getCheckoutStatus(sessionId);
        setStatus(res.data);
        if (res.data.payment_status === 'paid' || res.data.status === 'expired' || attempts >= maxAttempts) {
          setPolling(false);
        } else {
          setAttempts(a => a + 1);
        }
      } catch {
        if (attempts >= maxAttempts) setPolling(false);
        else setAttempts(a => a + 1);
      }
    };
    const timer = setTimeout(poll, attempts === 0 ? 500 : 2500);
    return () => clearTimeout(timer);
  }, [sessionId, polling, attempts]);

  const isPaid = status?.payment_status === 'paid';
  const isFailed = status && !isPaid && !polling;

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Header */}
      <header className="border-b" style={{ backgroundColor: 'var(--bg-dark)', borderColor: 'rgba(96,165,250,0.15)' }}>
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-14 flex items-center">
          <Link to="/" className="flex items-center gap-3" style={{ textDecoration: 'none' }}>
            <BookOpen className="w-5 h-5" style={{ color: 'var(--accent-light)' }} />
            <span className="text-lg tracking-tight" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-on-dark)', fontWeight: 600 }}>
              Quintessenza
            </span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center px-5 py-20">
        <div className="text-center max-w-md" data-testid="checkout-success-content">
          {polling && (
            <>
              <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: 'var(--accent)' }} />
              <h1 className="text-2xl tracking-tight mb-2" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>
                Verifica pagamento...
              </h1>
              <p className="text-sm" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>
                Stiamo verificando il tuo pagamento. Attendi qualche secondo.
              </p>
            </>
          )}

          {isPaid && (
            <>
              <CheckCircle2 className="w-16 h-16 mx-auto mb-5" style={{ color: 'var(--accent-success)' }} />
              <h1 className="text-3xl tracking-tight mb-3" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, color: 'var(--text-primary)' }}>
                Ordine Confermato!
              </h1>
              <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>
                Grazie per il tuo acquisto di <strong>{status.metadata?.product_title}</strong>
              </p>
              <p className="text-sm mb-6" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>
                Riceverai una conferma via email. {status.metadata?.is_digital === 'True'
                  ? 'Il prodotto digitale verra inviato all\'email indicata.'
                  : 'Il prodotto verra spedito all\'indirizzo fornito.'}
              </p>
              <div className="p-4 rounded-sm border mb-6" style={{ borderColor: 'var(--border-light)', backgroundColor: 'var(--bg-white)' }}>
                <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Totale pagato</p>
                <p className="text-2xl font-medium" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)' }}>
                  {(status.amount_total / 100).toFixed(2)} {status.currency?.toUpperCase()}
                </p>
              </div>
              <Link to="/">
                <Button className="rounded-sm px-6" style={{ backgroundColor: 'var(--accent)', color: '#fff' }} data-testid="back-to-store-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Torna al Negozio
                </Button>
              </Link>
            </>
          )}

          {isFailed && (
            <>
              <XCircle className="w-16 h-16 mx-auto mb-5" style={{ color: '#EF4444' }} />
              <h1 className="text-3xl tracking-tight mb-3" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>
                Pagamento non completato
              </h1>
              <p className="text-sm mb-6" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>
                Il pagamento non e' andato a buon fine. Riprova o contattaci per assistenza.
              </p>
              <Link to="/">
                <Button className="rounded-sm px-6" style={{ backgroundColor: 'var(--accent)', color: '#fff' }} data-testid="retry-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Torna al Negozio
                </Button>
              </Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
